import { effect, inject, Injectable } from '@angular/core';
import { Subscription } from 'rxjs';

import { AuthService } from './auth.service';
import { ThreadEventsService } from './thread-events.service';
import { ToastService } from './toast.service';

export type MemberConnectorChangedEvent = {
    type: 'member_connector_enabled' | 'member_connector_disabled';
    connector_name: string;
    connector_key: 'back_office' | 'inside_track' | 'arrowstream';
    actor_user_id: number | null;
    workspace_ids: number[];
};

export const MEMBER_CONNECTOR_TOAST_GATHER_MS = 2000;

/** Must exceed `_DEAL_CACHE_TTL_SECONDS` in backend/ddr/mcp/gates.py; change both together. */
export const MEMBER_CONNECTOR_FOLLOW_UP_RELOAD_MS = 31_000;

/** Keeps a member's connector access current when an admin changes it in the active workspace (CIQ-2567). */
@Injectable({ providedIn: 'root' })
export class MemberConnectorNotificationsService {
    private readonly auth = inject(AuthService);
    private readonly threadEvents = inject(ThreadEventsService);
    private readonly toast = inject(ToastService);

    private sseSub: Subscription | null = null;
    private pending: string[] = [];
    private flushTimer: ReturnType<typeof setTimeout> | null = null;
    private reloadTimer: ReturnType<typeof setTimeout> | null = null;
    private followUpTimer: ReturnType<typeof setTimeout> | null = null;

    // noinspection JSUnusedLocalSymbols
    private readonly _onAuth = effect(() => {
        if (this.auth.state().user) {
            this.start();
        } else {
            this.stop();
        }
    });

    start(): void {
        if (this.sseSub) {
            return;
        }
        this.sseSub = this.threadEvents.connect().subscribe(evt => {
            const e = evt as unknown as Partial<MemberConnectorChangedEvent> & { type?: string };
            if (e.type === 'member_connector_enabled' || e.type === 'member_connector_disabled') {
                this.apply(e);
            }
        });
    }

    stop(): void {
        this.sseSub?.unsubscribe();
        this.sseSub = null;
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
        }
        this.flushTimer = null;
        this.clearReloads();
        this.pending = [];
    }

    private clearReloads(): void {
        for (const timer of [this.reloadTimer, this.followUpTimer]) {
            if (timer) {
                clearTimeout(timer);
            }
        }
        this.reloadTimer = null;
        this.followUpTimer = null;
    }

    /** A second read after the cache TTL catches a first read served by another process's stale cache. */
    private scheduleReloads(): void {
        this.clearReloads();
        this.reloadTimer = setTimeout(() => {
            this.reloadTimer = null;
            this.auth.reloadCurrentUser();
            this.followUpTimer = setTimeout(() => {
                this.followUpTimer = null;
                this.auth.reloadCurrentUser();
            }, MEMBER_CONNECTOR_FOLLOW_UP_RELOAD_MS);
        }, MEMBER_CONNECTOR_TOAST_GATHER_MS);
    }

    private apply(event: Partial<MemberConnectorChangedEvent>): void {
        const user = this.auth.state().user;
        if (!user || user.workspace_id == null || !event.workspace_ids?.includes(user.workspace_id)) {
            return;
        }
        this.scheduleReloads();
        const name = event.connector_name?.trim();
        if (event.type !== 'member_connector_enabled' || !name || event.actor_user_id === user.user_id) {
            return;
        }
        if (!this.pending.includes(name)) {
            this.pending.push(name);
        }
        this.flushTimer ??= setTimeout(() => this.flush(), MEMBER_CONNECTOR_TOAST_GATHER_MS);
    }

    private flush(): void {
        this.flushTimer = null;
        const names = this.pending;
        this.pending = [];
        if (!names.length) {
            return;
        }
        const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
        void this.toast.success(`${list} ${names.length === 1 ? 'is' : 'are'} on for you.`);
    }
}
